// Sales area shell (office, Module 09 §4 — slice P3-A part 2). Sub-navigation
// over the foundation screens: the customer book (§4.1/§4.2), the price-list
// manager (§4.3), the off-cut discount classes and the margin guards.
// Registered at /sales; the index lands each principal on the first screen
// they can actually open (`salesLandingPath`).
//
// WHAT IS DELIBERATELY ABSENT (D-033 / spec 09 §4 slice ownership). Shipments
// have no office tab of their own: picking is the floor's (`/floor/ship`,
// §4.8) and the office reads shipments on the order. (This paragraph also
// named the stats board and analytics as absent until P3-E PR-E3 built §4.9,
// which is linked below.)
//
// THE PIPELINE (P3-E2 PR E2-1, spec 09 §4.14) is one entry, after Customers,
// for four addresses: the board, the deal list, the deal and the forecast.
// The three pages share their own tab strip below this one. Its gate is
// §7 narrowing 15's lineages (`canOpenPipeline`), so qc and the storekeeper,
// who hold `sales.read` for other screens, are offered no link the service
// would refuse.
// The quote book, the composer (quote AND order mode, since P3-D PR-D1 —
// D-283), the order book and the review inbox all EXIST and are linked below
// (P3-C-1 mounted the first three, P3-C-2 the override cards).
//
// THE TASK LIST AND THE SETTINGS SCREEN (P3-E PR-E2, D-292 rulings 1, 11).
// Until PR-E2 this header said there was no sales-settings screen because §4
// named none, and the GET/PUT pair existed only so the reminder engine had
// configuration to read. The engine now reads it every minute, and a knob a
// factory admin can reach only by `curl` is not finishable by a factory admin
// (doctrine 2): `/sales/settings` is the §2.22 row as a screen (read by the
// sales desk, edited by `sales.tier.manage`). `/sales/tasks` is the manager's
// task list — the rows the engine creates when a customer cannot be reached,
// and the ones a manager adds by hand.
//
// Nav gating mirrors §7 through hooks/permissions.ts, and the AUDIENCES are
// role sets where the spec's are (§4.1's customer-book audience is narrower
// than the `sales.read` holder set — qc and storekeeper hold the code for
// other surfaces); the margin-guard entry rides `sales.tier.manage` because
// its GET does (never a link into a dead 403 call, D-088).
//
// RENDER-ONCE (§5), AND THE WHOLE AREA AT ONCE — the waste-layout mechanism,
// verbatim: every entry is permission-decided, so the strip's WIDTH (and at
// 412 px its HEIGHT, since it wraps) is decided by a role feed that arrives
// after first paint. While the mirror is outstanding the area is a reserving
// skeleton whose box IS `.ds-tabs`'s pinned min-height; when it settles the
// whole subtree REMOUNTS (`key` swap) so nothing painted early ever moves.

import { useEffect } from "react";
import { Outlet, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { CardSkeleton, ForbiddenState } from "../../../components/states";
import { RouterTabs, type RouterTabDef } from "../../../components/tabs";
import { salesLandingPath, salesNavTabs, useSalesPermissions } from "./hooks/permissions";
import "./sales.css";

export function SalesLayout() {
  const { t } = useTranslation();
  const perms = useSalesPermissions();

  // ONE ENTRY TABLE (hooks/permissions.ts) — the strip, the app nav and the
  // index redirect all read it, so an entry cannot exist in one and not the
  // others (the two-gating-rules defect module 05 shipped and fixed). The
  // strip folds those screens into at most six tabs (D-445): a group's own
  // screens are a second strip under each of their headers
  // (components/group-tabs.tsx).
  const tabs: RouterTabDef[] = salesNavTabs(perms).map((tab) => ({
    to: tab.to,
    label: t(tab.labelKey),
    "data-testid": `sales-tab-${tab.key}`,
    ...(tab.activePaths !== undefined ? { activePaths: tab.activePaths } : {}),
  }));

  if (!perms.isResolved) {
    return (
      <div className="sales-area" key="sales-loading" data-testid="sales-loading">
        <div className="ds-tabs sales-subnav-skel" aria-hidden="true" />
        <div className="sales-body">
          <CardSkeleton lines={6} label={t("sales.common.loading")} />
        </div>
      </div>
    );
  }

  return (
    <div className="sales-area" key="sales-ready">
      <RouterTabs tabs={tabs} label={t("sales.nav.aria")} data-testid="sales-subnav" />
      <div className="sales-body">
        <Outlet />
      </div>
    </div>
  );
}

/**
 * `/sales` — land this principal on the first screen they can actually open.
 *
 * A COMPONENT, not a `beforeLoad`: `beforeLoad` runs outside React and cannot
 * read the role feed, so a static redirect would send a `sales.tier.manage`-
 * only principal to a customer list they are refused (the module-05 defect,
 * fixed there, not re-shipped here). The parent shell has already reserved
 * the layout, so this renders nothing while it decides.
 */
export function SalesIndexRedirect() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const perms = useSalesPermissions();
  const to = salesLandingPath(perms);

  useEffect(() => {
    if (to !== undefined) void navigate({ to, replace: true });
  }, [to, navigate]);

  if (to !== undefined) return null;
  return <ForbiddenState title={t("sales.access.title")} body={t("sales.access.area")} />;
}
